import React, { useEffect, useState } from "react";
import {
  MapPin,
  Users,
  BarChart3,
  Filter,
  UserCheck,
  Layers,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import axios from "axios";
import { apiurl } from "../../config/config.js";
import Layout, { Container } from "../../components/layout.js";

const today = new Date().toISOString().split("T")[0];

export default function RecruitmentAnalytics() {
  const [filters, setFilters] = useState({
    startDate: today,
    endDate: today,
    location: "",
  });

  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState({
    global: { totalApplications: 0, statuses: {} },
    byLocation: [],
    byRecruiter: [],
  });

  const [table, setTable] = useState([]);
  const [openRec, setOpenRec] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${apiurl}/api/analytic/recruitment`,
        { params: filters }
      );

      if (data?.cards) setCards(data.cards);
      if (data?.tables?.recruiterPriority)
        setTable(data.tables.recruiterPriority);
    } catch (err) {
      console.error("Analytics fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const applyFilters = () => fetchData();

  const renderStatusBadges = (statuses = {}) => (
    <div className="flex flex-wrap gap-2 mt-2">
      {Object.entries(statuses).map(([status, count]) => (
        <span
          key={status}
          className="px-3 py-1 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200 capitalize"
        >
          {status}: {count}
        </span>
      ))}
    </div>
  );

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="p-6 space-y-10 bg-gradient-to-br from-slate-50 to-gray-100 min-h-screen">

          {/* HEADER */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <BarChart3 className="text-blue-600" />
              Recruitment Analytics
            </h1>
          </div>

          {/* FILTERS */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white p-4 rounded-2xl shadow-sm">
            <input
              type="date"
              className="border p-2 rounded w-full"
              value={filters.startDate}
              onChange={(e) =>
                setFilters({ ...filters, startDate: e.target.value })
              }
            />
            <input
              type="date"
              className="border p-2 rounded w-full"
              value={filters.endDate}
              onChange={(e) =>
                setFilters({ ...filters, endDate: e.target.value })
              }
            />
            <input
              placeholder="Location (optional)"
              className="border p-2 rounded w-full"
              value={filters.location}
              onChange={(e) =>
                setFilters({ ...filters, location: e.target.value })
              }
            />
            <button
              onClick={applyFilters}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-4 py-2 flex items-center justify-center gap-2"
            >
              <Filter size={18} /> Apply
            </button>
          </div>

          {/* GLOBAL SUMMARY */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border">
            <div className="flex items-center gap-2 text-gray-700 font-semibold">
              <Layers className="text-indigo-600" /> All Applications
            </div>
            <div className="text-4xl font-bold mt-3 text-gray-800">
              {cards.global?.totalApplications || 0}
            </div>
            <div className="text-sm text-gray-500">Total Applications</div>
            {renderStatusBadges(cards.global?.statuses)}
          </div>

          {/* LOCATION CARDS */}
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <MapPin className="text-blue-600" /> Location-wise
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {cards.byLocation.map((loc) => (
                <div
                  key={loc.location || "unknown"}
                  className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition"
                >
                  <div className="flex items-center gap-2 text-gray-500">
                    <MapPin size={18} className="text-blue-500" />
                    {loc.location || "Unknown"}
                  </div>

                  <div className="text-3xl font-bold mt-2 text-gray-800">
                    {loc.totalApplications || 0}
                  </div>

                  <div className="text-sm text-gray-500">
                    Applications
                  </div>

                  {renderStatusBadges(loc.statuses)}
                </div>
              ))}
            </div>
          </div>
{/* ---------------- RECRUITER TABLE ---------------- */}
<div className="bg-white rounded-2xl shadow-sm border overflow-x-auto">
  <div className="p-4 border-b font-semibold flex items-center gap-2">
    <Users size={18} className="text-purple-600" />
    Recruiter-wise Breakdown
  </div>

  <table className="w-full text-sm">
    <thead className="bg-gray-50">
      <tr>
        <th className="p-3 text-left">Recruiter</th>
        <th className="p-3 text-left">ID</th>
        <th className="p-3 text-center">Total Apps</th>
        <th className="p-3 text-left">Locations</th>
        <th className="p-3 text-left">Status Breakdown</th>
      </tr>
    </thead>

    <tbody>
      {loading ? (
        <tr>
          <td colSpan="5" className="p-6 text-center text-gray-500">
            Loading...
          </td>
        </tr>
      ) : cards.byRecruiter.length === 0 ? (
        <tr>
          <td colSpan="5" className="p-6 text-center text-gray-400">
            No data found
          </td>
        </tr>
      ) : (
        cards.byRecruiter.map((rec) => (
          <tr key={rec.recruiterId} className="border-t hover:bg-gray-50">
            {/* Recruiter Name */}
            <td className="p-3 font-medium text-gray-800">
              {rec.recruiterName || "Unassigned"}
            </td>

            {/* Recruiter ID */}
            <td className="p-3 text-xs text-gray-400">
              {rec.recruiterId || "N/A"}
            </td>

            {/* Total Applications */}
            <td className="p-3 text-center font-semibold text-blue-700">
              {rec.totalApplications || 0}
            </td>

            {/* Locations (badge list) */}
            <td className="p-3">
              <div className="flex flex-wrap gap-2">
                {(rec.locations || []).map((loc) => (
                  <span
                    key={loc.location}
                    className="px-2 py-1 text-xs rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
                  >
                    {loc.location} ({loc.total})
                  </span>
                ))}
              </div>
            </td>

            {/* Status Breakdown */}
            <td className="p-3">
              <div className="flex flex-wrap gap-2">
                {Object.entries(rec.statuses || {}).map(
                  ([status, count]) => (
                    <span
                      key={status}
                      className="px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200 capitalize"
                    >
                      {status}: {count}
                    </span>
                  )
                )}
              </div>
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
</div>


          {loading && (
            <div className="text-center text-gray-500 py-6">
              Loading analytics...
            </div>
          )}
        </div>
      </Container>
    </Layout>
  );
}